import { CHANNEL_MAP } from '../api/channelMap';
import { formatRelativeDayLabel } from './relativeDay';

/** Turn EPG xmltv channel ids into readable names when map lookup misses. */
function humanizeEpgChannelId(rawId) {
  if (!rawId) return null;

  const id = rawId.replace(/@\w+$/, '');

  const sportTv = id.match(/^SportTV(\d+)\.pt$/i);
  if (sportTv) return `Sport TV ${sportTv[1]}`;

  const dazn = id.match(/^DAZN(\d+)\.uk$/i);
  if (dazn) return `DAZN ${dazn[1]}`;

  const rtp = id.match(/^RTP(\d+)\.pt$/i);
  if (rtp) return `RTP ${rtp[1]}`;

  // Generic: strip TLD, split camelCase / digits
  const base = id.replace(/\.(pt|uk|us|ua)$/i, '');
  return base
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Za-z])(\d)/g, '$1 $2')
    .replace(/(\d)([A-Za-z])/g, '$1 $2')
    .trim();
}

function lookupEpgChannel(channelId) {
  if (!channelId) return null;
  if (CHANNEL_MAP[channelId]) return CHANNEL_MAP[channelId];

  const withoutSuffix = channelId.replace(/@\w+$/, '');
  if (CHANNEL_MAP[withoutSuffix]) return CHANNEL_MAP[withoutSuffix];

  return humanizeEpgChannelId(channelId);
}

/** Display name for the card channel row / platform meta strip. */
export function getChannelDisplayName(event) {
  if (!event) return null;

  if (event.platform === 'youtube') {
    return event.channelTitle || 'YouTube';
  }

  if (event.platform === 'twitch') {
    // Backend stores broadcaster login/name on `title`
    return event.title || event.channelTitle || 'Twitch';
  }

  if (event.platform === 'kick') {
    return event.channelTitle || event.channelLogin || 'Kick';
  }

  if (event.platform === 'concert') {
    return [event.venueName, event.city].filter(Boolean).join(' · ') || 'Concert';
  }

  if (event.platform === 'EPG') {
    return lookupEpgChannel(event.channelId || event.id);
  }

  const id = event.channelId || event.id;
  return id ? (CHANNEL_MAP[id] || humanizeEpgChannelId(id)) : null;
}

/** Stream/video title for YouTube, Twitch, and Kick cards. */
export function getStreamTitle(event) {
  if (!event) return null;
  if (event.platform === 'youtube') return event.title || null;
  if (event.platform === 'twitch') return event.channelTitle || event.title || null;
  if (event.platform === 'kick') return event.title || null;
  return null;
}

export function formatEventDate(start) {
  if (!start) return '';
  const d = new Date(start);
  if (Number.isNaN(d.getTime())) return '';
  return formatRelativeDayLabel(d, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatEventTimeRange(start, end) {
  if (!start) return '';
  const startDate = new Date(start);
  if (Number.isNaN(startDate.getTime())) return '';
  const timeOpts = { hour: '2-digit', minute: '2-digit', hour12: false };
  const startTime = startDate.toLocaleTimeString(undefined, timeOpts);
  if (!end) return startTime;
  const endDate = new Date(end);
  if (Number.isNaN(endDate.getTime())) return startTime;
  const endTime = endDate.toLocaleTimeString(undefined, timeOpts);
  return `${startTime} – ${endTime}`;
}
